import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import moment from 'moment';
import React from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import {NotebookSectionColor} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getDefaultDateForInsertEntryDay} from 'notebook/entries/BaseEntryUtil';
import EditSeparatorDropdown from 'notebook/entries/core-ui/EditSeparatorDropdown';
import SectionColorPicker from 'notebook/entries/SectionColorPicker';
import DaySelectorModal from 'notebook/entry-templates/core-ui/DaySelectorModal';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import {preventAccordionToggleProps} from 'ui-library/Accordion';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import * as ModalContainer from 'ui-library/ModalContainer';

export const HIGH_CONTRAST_SECTION_COLORS = [
  NotebookSectionColor.CONTRAST_BLUE,
  NotebookSectionColor.CONTRAST_GREEN,
  NotebookSectionColor.CONTRAST_GREY,
  NotebookSectionColor.CONTRAST_INDIGO,
  NotebookSectionColor.CONTRAST_ORANGE,
  NotebookSectionColor.CONTRAST_RED,
  NotebookSectionColor.CONTRAST_VIOLET,
  NotebookSectionColor.CONTRAST_YELLOW,
];

interface DayItem {
  index: number;
  cid: string;
  date?: string;
  title?: string;
  sectionColor?: string;
}

interface DaySeparatorProps {
  day: DayItem;
  forTemplate: boolean;
  getTextForDate: (date: moment.MomentInput) => string;
  isDateChangeable?: boolean;
  isDateAllowed?: boolean;
  onDelete?: (cid: string, e?: React.MouseEvent) => void;
  previousDate?: string;
  nextDate?: string;
  isDayOnly?: boolean;
  isProtected?: boolean;
  title?: string;
  onTitleChange?: (cid: string, title: string) => boolean | void;
  onDayChange: (cid: string, title: string | undefined, date: string) => void;
  onColorChange?: (cid: string, color: NotebookSectionColor) => void;
  getDateBoundaries: (index: number) => {previousDate: string; nextDate: string};
}

function enableTooltipsForMonth(inputNode: Element): void {
  $(inputNode).datepicker('widget').find('td[title]').tooltip({container: 'body'});
}

const MAX_TITLE_LENGTH = 255;
const TITLE_LENGTH_BUFFER = 10;

class DaySeparator extends React.PureComponent<DaySeparatorProps> {
  handleChange = (newDate: string): null => {
    const dateError = this.getDateError(newDate);
    if (dateError) {
      notifications.warn(dateError);
      return null;
    }
    this.props.onDayChange(this.props.day.cid, this.props.title, newDate);
    return null;
  };

  getDateError = (date: string | Date): string | null => {
    const {previousDate, nextDate, getTextForDate} = this.props;
    const dateAsMoment = moment(date);
    const previousDateAsMoment = moment(previousDate);
    const nextDateAsMoment = moment(nextDate);

    if (!this.props.isDayOnly) {
      if (previousDate && !dateAsMoment.isSameOrAfter(previousDateAsMoment, 'day')) {
        return `Date must be the same as or follow the entry's previous date, ${getTextForDate(
          previousDate
        )}.`;
      }
      if (nextDate && !dateAsMoment.isSameOrBefore(nextDateAsMoment, 'day')) {
        return `Date must be the same as or precede the entry's next date, ${getTextForDate(nextDate)}.`;
      }
    } else {
      if (previousDate && !dateAsMoment.isAfter(previousDateAsMoment)) {
        return `Date must follow the entry's previous date, ${getTextForDate(previousDate)}.`;
      }
      if (nextDate && !dateAsMoment.isBefore(nextDateAsMoment)) {
        return `Date must precede the entry's next date, ${getTextForDate(nextDate)}.`;
      }
    }
    return null;
  };

  handleDelete = (e?: React.MouseEvent): void => {
    this.props.onDelete?.(this.props.day.cid, e);
  };

  /* For JQuery datepicker, called on each date before it is displayed
  * @param date {Object}: a JS Date object
  * @return [isSelectable, cssClass, tooltip] {[Boolean, String, String]}:
      isSelectable: true/false indicating whether or not this date is selectable
      cssClass: a CSS class name to add to the date's cell or "" for the default presentation
      tooltip: an optional popup tooltip for this date
  */
  beforeShowDay = (date: Date): [boolean, string, string | null] => {
    const tooltip = this.getDateError(date);
    const isSelectable = tooltip == null;
    return [isSelectable, '', tooltip];
  };

  addDatePicker = (): void => {
    const inputNode = ReactDOM.findDOMNode(this.refs.hiddenInput);
    if (inputNode && inputNode instanceof Element) {
      $(inputNode).datepicker({
        onSelect: this.handleChange,
        dateFormat: 'yy-mm-dd',
        defaultDate: this.props.day.date,
        beforeShowDay: this.beforeShowDay,
        onChangeMonthYear: () => {
          // needs to be deferred because this is called async before the days exist in the DOM
          setTimeout(() => {
            enableTooltipsForMonth(inputNode);
          }, 0);
        },
      });
    }
  };

  removeDatePicker = (): void => {
    const inputNode = ReactDOM.findDOMNode(this.refs.hiddenInput);
    if (inputNode) {
      $(inputNode).datepicker('hide');
    }
  };

  componentDidMount(): void {
    this.addDatePicker();
  }

  componentDidUpdate(prevProps: DaySeparatorProps): void {
    if (this.props.isDateChangeable && !prevProps.isDateChangeable) {
      this.addDatePicker();
    } else if (!this.props.isDateChangeable && prevProps.isDateChangeable) {
      this.removeDatePicker();
    }
  }

  dayClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (this.props.forTemplate && this.props.day.date) {
      ModalContainer.open(
        DaySelectorModal,
        <DaySelectorModal
          initialDate={this.props.day.date}
          setDate={this.handleChange}
          onRequestClose={() => {
            ModalContainer.close(DaySelectorModal);
          }}
        />
      );
    } else {
      const inputNode = ReactDOM.findDOMNode(this.refs.hiddenInput);
      if (inputNode && inputNode instanceof Element) {
        $(inputNode).datepicker('setDate', this.props.day.date);
        $(inputNode).datepicker('show');
        enableTooltipsForMonth(inputNode);
      }
    }
  };

  handleComplete = (editedName: string): boolean | void => {
    if (editedName.length > MAX_TITLE_LENGTH) {
      notifications.warn(
        `Section name must be less than ${MAX_TITLE_LENGTH} characters. ${editedName} has ${editedName.length} characters.`
      );
      return true;
    }
    return this.props.onTitleChange?.(this.props.day.cid, editedName);
  };

  handleColor = (color: NotebookSectionColor): void => {
    this.props.onColorChange?.(this.props.day.cid, color);
  };

  isHighContrast = (color?: string): boolean => {
    if (color) {
      return HIGH_CONTRAST_SECTION_COLORS.includes(color as NotebookSectionColor);
    }
    return false;
  };

  renderDelete = (): React.ReactNode => {
    if (
      this.props.onDelete != null &&
      !this.props.isProtected &&
      !BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS
    ) {
      return (
        <a
          id="day-separator-delete-button"
          className={`round-btn ${
            this.props.isDayOnly ? 'daySeparator' : 'noteSection'
          }-delete u-danger-pointer`}
          onClick={this.handleDelete}
        >
          ×
        </a>
      );
    }
    return null;
  };

  onMenuOptionSelect = (optionType: string): void => {
    if (optionType === 'ADD_NAME') {
      this.props.onTitleChange?.(this.props.day.cid, 'Untitled');
    } else if (optionType === 'REMOVE_NAME') {
      this.props.onTitleChange?.(this.props.day.cid, '');
    } else if (optionType === 'ADD_DATE') {
      const {previousDate, nextDate} = this.props.getDateBoundaries(this.props.day.index);
      const date = getDefaultDateForInsertEntryDay('', previousDate, nextDate, this.props.forTemplate);
      this.props.onDayChange(this.props.day.cid, this.props.title, date);
    } else if (optionType === 'REMOVE_DATE') {
      this.props.onDayChange(this.props.day.cid, this.props.title, '');
    } else if (optionType === 'DELETE') {
      this.handleDelete();
    }
  };

  render(): React.ReactNode {
    const isDeleteAllowed = !this.props.isProtected && this.props.onDelete;

    if (!this.props.isDayOnly) {
      return (
        <div data-scroll-id={this.props.day.cid} className="noteSection">
          {this.props.title && (
            <div {...preventAccordionToggleProps()} className={css(styles.titleWrapper)}>
              <EditableTextField
                inputClassName="noteSection-title"
                value={this.props.title != null ? this.props.title : ''}
                emptyValueAllowed={true}
                textPlaceholder=""
                requestChange={this.handleComplete}
                initialIsBeingEdited={false}
                isDisabled={this.props.isProtected}
                dataTestAttrs={{component: 'DaySeparator', element: 'title'}}
                maxLength={MAX_TITLE_LENGTH + TITLE_LENGTH_BUFFER}
                breakLines={true}
              />
            </div>
          )}
          {this.props.day.date && (
            <div className="noteSection-inputWrapper">
              <input className="noteSection-inputWrapper-hiddenInput" ref="hiddenInput" type="input" />
              <span
                className={classNames(
                  'noteSection-date',
                  this.isHighContrast(this.props.day.sectionColor) && 'noteSection-highContrast'
                )}
                onClick={this.props.isDateChangeable ? this.dayClick : undefined}
                {...getTestProps({component: 'DaySeparator', element: 'date'})}
              >
                <BenchlingIcon icon="calendar" />
                {this.props.getTextForDate(this.props.day.date)}
              </span>
            </div>
          )}
          {!this.props.isProtected && (
            <div className="noteSection-edit">
              <SectionColorPicker
                selectedColor={this.props.day.sectionColor ?? NotebookSectionColor.DEFAULT}
                onSelectColor={this.handleColor}
              />
              <EditSeparatorDropdown
                hasTitle={Boolean(this.props.title)}
                hasDate={Boolean(this.props.day.date)}
                isDateAllowed={Boolean(this.props.isDateAllowed)}
                isDeleteAllowed={Boolean(isDeleteAllowed)}
                onOptionSelect={this.onMenuOptionSelect}
              />
            </div>
          )}
          {this.renderDelete()}
        </div>
      );
    }
    return (
      <div data-scroll-id={this.props.day.cid} className="daySeparator">
        <div className="daySeparator-inputWrapper">
          <input className="daySeparator-inputWrapper-hiddenInput" ref="hiddenInput" type="input" />
        </div>
        <span
          className="daySeparator-date"
          onClick={(event) => {
            this.dayClick(event);
          }}
          {...getTestProps({component: 'DaySeparator', element: 'date'})}
        >
          {this.props.getTextForDate(this.props.day.date)}
        </span>
        {this.renderDelete()}
        <div className="daySeparator-line" />
      </div>
    );
  }
}

const styles = StyleSheet.create({
  titleWrapper: {
    maxWidth: '65%',
  },
});

export default DaySeparator;
